import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";

import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import {
	useDeleteUserMutation,
	useLazyGetUsersQuery,
	useLoginAsMutation,
	useReinviteUserMutation,
	useUpdateUserLinkedinAllowedMutation,
} from "@/pages/Private/redux/admin/admin.api";

import { Table, TableColumn } from "@/components/Table/Table";

import { LoadingOverlay, Modal, PermissionCheck } from "@/components";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { useTableSearch } from "@/utils";
import { runOrToast } from "@/utils/runOrToast";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { PermissionRoles } from "@/enum";

import { authSelector, setAuth } from "@/pages/Public/redux/auth.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { adminSelector, setUsersSorting } from "@/pages/Private/redux/admin/admin.slice";

import { SortParams } from "@/types/types";

import { InputField } from "@/components/InputField/InputField";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useSlotsAvailableQuery } from "@/pages/Private/redux/unipile/unipile.api";
import { useLazyGetPendingBySenderQuery } from "@/pages/Private/redux/schedule/schedule.api";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { CampaignAction, CampaignPending } from "../../Account/schema/pendingSchedules";
import { PendingSchedulesDialog } from "../../Account/components/PendingSchedulesDialog";

import { InviteUserModal } from "./InviteUserModal";
import { EditUserModal } from "./EditUserModal";
import { EditLinkedinSettings } from "./EditLinkedinSettings";
import { EmailsTable } from "./EmailsTable";
import { FeaturesCodes } from "../enum/codes";
import { buildUsersTableColumns } from "./usersTableColumns";

export const UsersTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`users.${key}`);

	const { id } = useParams();

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const { data: slotData } = useSlotsAvailableQuery(id ? +id : undefined);

	const { sortingUsers, users, meta } = useAppSelector(adminSelector);
	const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
	const [showEmailsModal, setShowEmailsModal] = useState<boolean>(false);
	const [showLinkedinSettingsModal, setShowLinkedinSettingsModal] = useState<boolean>(false);
	const dispatch = useAppDispatch();
	const userTokenObj = useAppSelector(authSelector);
	const navigate = useNavigate();
	const [showEditModal, setShowEditModal] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showReinviteModal, setShowReinviteModal] = useState<boolean>(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [loginAsUser] = useLoginAsMutation();
	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query } = useTableSearch();

	const [getUsers, { isLoading: isUsersLoading, isFetching: isUsersFetching }] =
		useLazyGetUsersQuery();

	useEffect(() => {
		getUsers({
			page: page,
			limit,
			searchValue: query,
			companyId: id ? +id : undefined,
			sorting: sortingUsers,
		});
	}, [page, limit, query, id, sortingUsers]);

	const handleSorting = (sortParams: SortParams) => {
		setPage(1);

		dispatch(setUsersSorting(sortParams));
	};

	const [deleteUser, { isLoading }] = useDeleteUserMutation();
	const [reinviteUser, { isLoading: isReinviteLoading }] = useReinviteUserMutation();
	const [updateUserLinkedinAllowed, { isLoading: isTogglingLinkedin }] =
		useUpdateUserLinkedinAllowedMutation();
	const [getPendingBySender, { isFetching: isCheckingPending }] = useLazyGetPendingBySenderQuery();

	const [showPendingDialog, setShowPendingDialog] = useState(false);
	const [pendingCampaigns, setPendingCampaigns] = useState<CampaignPending[]>([]);
	const [pendingTotalCount, setPendingTotalCount] = useState(0);
	const [pendingUser, setPendingUser] = useState<User | null>(null);
	const [pendingAction, setPendingAction] = useState<"linkedin-toggle" | "delete">(
		"linkedin-toggle"
	);

	const loginAs = async (user: User) => {
		localStorage.setItem("initialUser", JSON.stringify(userTokenObj));

		const newUser = await loginAsUser(user.id).unwrap();

		dispatch(setAuth(newUser));

		window.location.href = `/app/lists`;
	};

	// PREC-4429: the resend says whether the invitation went out, and to which address.
	const handleReinviteUser = async () => {
		if (!selectedUser) {
			return;
		}

		const sent = await runOrToast(
			() => reinviteUser(selectedUser.id).unwrap(),
			t("inviteUser.resend.failed")
		);

		if (sent) {
			showGlobalToast(t("inviteUser.resend.sent", { email: selectedUser.email }), 6000, "success");
			setShowReinviteModal(false);
		}
	};

	const checkPendingSchedules = async (user: User) => {
		const allCampaigns: CampaignPending[] = [];
		let total = 0;

		// Check LinkedIn sender
		const linkedinResult = await getPendingBySender({ userId: user.id }).unwrap();

		for (const campaign of linkedinResult.campaigns) {
			allCampaigns.push({
				...campaign,
				originSender: {
					name: `${user.firstName} ${user.lastName}`,
					type: "linkedin",
				},
			});
			total += campaign.pendingCount;
		}

		// Check each email sender
		const emails = user.emails ?? [];

		for (const email of emails) {
			const emailResult = await getPendingBySender({
				userId: user.id,
				emailId: email.id,
			}).unwrap();

			for (const campaign of emailResult.campaigns) {
				allCampaigns.push({
					...campaign,
					originSender: {
						name: `${email.senderFirstName} ${email.senderLastName}`,
						type: "email",
						emailAddress: email.email,
					},
				});
				total += campaign.pendingCount;
			}
		}

		return { allCampaigns, total };
	};

	const handleChangeLinkedinAllowed = async (user: User) => {
		if (user.linkedinAllowed) {
			try {
				const { allCampaigns, total } = await checkPendingSchedules(user);

				if (total > 0) {
					setPendingCampaigns(allCampaigns);
					setPendingTotalCount(total);
					setPendingUser(user);
					setPendingAction("linkedin-toggle");
					setShowPendingDialog(true);

					return;
				}
			} catch {
				// If checking fails, proceed with toggle anyway
			}
		}

		await updateUserLinkedinAllowed({ id: user.id }).unwrap();
	};

	const handleConfirmPendingAction = async (campaignActions: CampaignAction[]) => {
		if (!pendingUser) {
			return;
		}

		const filteredActions = campaignActions.filter((a) => a.action !== "skip");

		if (pendingAction === "linkedin-toggle") {
			await updateUserLinkedinAllowed({
				id: pendingUser.id,
				...(filteredActions.length > 0 ? { campaignActions: filteredActions } : {}),
			}).unwrap();
		} else {
			await deleteUser({
				id: pendingUser.id,
				...(filteredActions.length > 0 ? { campaignActions: filteredActions } : {}),
			}).unwrap();
		}

		setShowPendingDialog(false);
		setPendingCampaigns([]);
		setPendingTotalCount(0);
		setPendingUser(null);
	};

	const handleCancelPendingAction = () => {
		setShowPendingDialog(false);
		setPendingCampaigns([]);
		setPendingTotalCount(0);
		setPendingUser(null);
	};

	const handleOpenLinkedinSettings = (user: User) => {
		setShowLinkedinSettingsModal(true);
		setSelectedUser(user);
	};

	const columns: TableColumn[] = useMemo(
		() =>
			buildUsersTableColumns({
				t,
				sortingUsers,
				slotData,
				currentUserId: userTokenObj.user?.id,
				companyId: id,
				isSequencerActive,
				// Wrapped, not passed by reference: handleEditUser / handleDeleteModal are
				// declared below this memo, so reading them eagerly hits the TDZ.
				onToggleLinkedinAllowed: (row) => handleChangeLinkedinAllowed(row),
				onOpenLinkedinSettings: (row) => handleOpenLinkedinSettings(row),
				onOpenEmails: (row) => {
					setShowEmailsModal(true);
					setSelectedUser(row);
				},
				onReinvite: (row) => {
					setShowReinviteModal(true);
					setSelectedUser(row);
				},
				onLoginAs: (row) => loginAs(row),
				onEdit: (row) => handleEditUser(row),
				onDelete: (row) => handleDeleteModal(row),
				onNavigate: (path) => navigate(path),
			}),
		[id, t, sortingUsers, slotData, isSequencerActive]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(0);
	};

	const handleEditUser = (row: User) => {
		setShowEditModal(true);
		setSelectedUser(row);
	};

	const handleDeleteModal = async (row: User) => {
		try {
			const { allCampaigns, total } = await checkPendingSchedules(row);

			if (total > 0) {
				setPendingCampaigns(allCampaigns);
				setPendingTotalCount(total);
				setPendingUser(row);
				setPendingAction("delete");
				setShowPendingDialog(true);

				return;
			}
		} catch {
			// If checking fails, proceed with delete confirmation anyway
		}

		setShowDeleteModal(true);
		setSelectedUser(row);
	};

	const handleDeleteUser = async () => {
		try {
			await deleteUser({ id: selectedUser?.id ?? 0 }).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleCloseEditModal = () => {
		setShowEditModal(false);
		setSelectedUser(null);
	};

	return !users && !isUsersLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="flex items-end align-baseline justify-between  mb-8">
				<div className="flex items-end align-baseline justify-between w-1/2">
					<div className="flex-grow mr-4 ">
						<InputField
							handleChange={(e) => setSearchValue(e.target.value)}
							iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
							name="search"
							placeholder={t("basics.search", { something: ts("search") })}
							value={searchValue}
						/>
					</div>
				</div>
				<div className="flex items-center justify-end gap-2 mb-3">
					<PermissionCheck requiredPermissions={[PermissionRoles.INVITE_USER]}>
						<Button
							color={ButtonColor.ACTION}
							size={ButtonSize.ML}
							testId="new-user-button"
							title={t("inviteUser.button")}
							onClick={() => setIsInviteModalOpen(true)}
						/>
					</PermissionCheck>
				</div>
			</div>
			<Table
				columns={columns}
				data={users || []}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleFetchPage={(page) => setPage(page)}
				handleSorting={handleSorting}
				hidePagination={false}
				isFetching={isUsersFetching}
				isLoading={isUsersLoading}
				limit={limit}
				noDataText={t("noData")}
				overallCount={meta?.itemCount ?? 0}
				page={page}
				totalPages={meta?.pageCount ?? 0}
			/>

			{showEditModal && (
				<EditUserModal
					companyId={id ? +id : undefined}
					handleClose={handleCloseEditModal}
					isVisible={showEditModal}
					user={selectedUser}
				/>
			)}

			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteUser}
					isLoading={isLoading}
					isOpened={showDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={t("modals.deleteUser.title")}
				>
					<TextField
						fullWidth
						defaultValue={selectedUser?.email}
						inputProps={{ readOnly: true }}
						label={t("basics.email")}
					/>
				</Modal>
			)}

			{showReinviteModal && selectedUser && (
				<Modal
					handleClose={() => setShowReinviteModal(false)}
					handleSave={() => handleReinviteUser()}
					isLoading={isReinviteLoading}
					isOpened={showReinviteModal}
					submitButtonText="Resend invite"
					title={`Resend invite`}
				>
					<div className="text-sm text-text-meta">
						Are you sure you want to resend the invite to{" "}
						<span className="inline font-bold">{selectedUser?.email}</span>?
					</div>
				</Modal>
			)}
			{isInviteModalOpen && (
				<InviteUserModal
					companyId={id ? +id : undefined}
					isOpen={isInviteModalOpen}
					isSequencerActive={isSequencerActive}
					setIsOpen={setIsInviteModalOpen}
					slotData={slotData}
				/>
			)}
			{showLinkedinSettingsModal && selectedUser && (
				<EditLinkedinSettings
					handleClose={() => setShowLinkedinSettingsModal(false)}
					isVisible={showLinkedinSettingsModal}
					user={selectedUser}
				/>
			)}
			{showEmailsModal && selectedUser && (
				<EmailsTable
					handleClose={() => setShowEmailsModal(false)}
					isVisible={showEmailsModal}
					slotData={slotData}
					user={selectedUser}
				/>
			)}
			<PendingSchedulesDialog
				campaigns={pendingCampaigns}
				isLoading={isTogglingLinkedin || isLoading || isCheckingPending}
				isOpen={showPendingDialog}
				onClose={handleCancelPendingAction}
				onConfirm={handleConfirmPendingAction}
				senderType="user"
				totalCount={pendingTotalCount}
			/>
		</div>
	);
};
